/**
 * The active tenant, and the people in it.
 *
 * Both are small, both are read on every screen, and both change rarely — which
 * is exactly the shape a listener is best at: the cost is one subscription and
 * the payoff is that a role change reaches the other person's phone without
 * them reopening the app. See useLiveQuery.ts.
 */
import {
  type Member,
  paths,
  type Role,
  type Tenant,
  type ViewScope,
} from '@puppy-doctor/platform';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { SNAPSHOT_OPTIONS } from '@/api/converter.ts';
import { memberConverter, tenantConverter } from '@/api/converters.ts';
import { useLiveCollection, useLiveDocument } from '@/api/useLiveQuery.ts';
import { db, functions } from '@/firebase.ts';

const tenantRef = (tenantId: string) =>
  doc(db, paths.tenant(tenantId)).withConverter(tenantConverter);

export const tenantQueryOptions = (tenantId: string) =>
  queryOptions({
    queryKey: ['tenant', tenantId] as const,
    queryFn: async (): Promise<Tenant | null> => {
      const snap = await getDoc(tenantRef(tenantId));
      return snap.exists() ? snap.data(SNAPSHOT_OPTIONS) : null;
    },
  });

export const useTenantDoc = (tenantId: string) => {
  useLiveDocument(['tenant', tenantId], tenantRef(tenantId));
  return useQuery(tenantQueryOptions(tenantId));
};

/**
 * Ordered by name rather than by role: a list of people is scanned for a person.
 * The role is shown on each row and is what the reader is checking once they
 * have found the name, not what they are searching by.
 */
const membersQuery = (tenantId: string) =>
  query(
    collection(db, paths.members(tenantId)).withConverter(memberConverter),
    orderBy('displayName', 'asc'),
  );

export const membersQueryOptions = (tenantId: string) =>
  queryOptions({
    queryKey: ['members', tenantId] as const,
    queryFn: async (): Promise<Member[]> => {
      const snap = await getDocs(membersQuery(tenantId));
      return snap.docs.map((d) => d.data(SNAPSHOT_OPTIONS));
    },
  });

export const useMembers = (tenantId: string) => {
  useLiveCollection(['members', tenantId], membersQuery(tenantId));
  return useQuery(membersQueryOptions(tenantId));
};

/**
 * Change what somebody may do here, or remove them — `role: null` is the removal.
 *
 * A callable, because `tenants/{id}/members/{uid}` is a document the rules deny
 * to every client including an owner. The reason is a count they cannot do: the
 * last owner must not be able to demote or remove themselves, and a tenant with
 * no owner is unadministrable with no way back. See functions/src/membership.ts.
 *
 * Awaited, unlike the Firestore writes in this app, and the difference is real:
 * this one can be REFUSED on a rule a client cannot evaluate ("promote somebody
 * else first"), and the person who tapped needs to be told. Offline it simply
 * fails, which is honest — the check needs the server.
 */
export const changeMemberRole = async (
  tenantId: string,
  uid: string,
  role: Role | null,
  /**
   * What a viewer may see, when the new role is `viewer`. Ignored for every
   * other role, which sees all of it — the function writes the full list rather
   * than leaving the field behind to be read as a restriction later.
   *
   * Required, with no default. An empty array means an observer who is shown
   * nothing, and a default that quietly produced one — or, worse, a default the
   * function read as "all of them" — is the kind of argument nobody passes and
   * everybody assumes. The caller always knows what the row already carries.
   */
  scopes: readonly ViewScope[],
): Promise<void> => {
  const call = httpsCallable<
    { tenantId: string; uid: string; role: Role | null; scopes: readonly ViewScope[] },
    { ok: boolean }
  >(functions, 'changeMemberRole');
  await call({ tenantId, uid, role, scopes });
};
